// One grievance: what he said, every step, and the step that is next.
//
// Every button here is offered because Postgres said so (grievance_get_v1,
// `can`), and every one of them is checked again when it is pressed. A person
// who may not see a grievance gets the restricted view: that a matter exists,
// its reference, who holds it and when it is due, and nothing about it.
//
// THE ANSWER CANNOT BE SENT HALF WRITTEN. What was found, what Fleetly will do,
// by when, and the next step (31.12). The button stays disabled until all four
// are filled, and Postgres refuses the same thing in the same words.
import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { AlertTriangle, ExternalLink, FileText, Lock, Mail, Paperclip, Plus, Scale, Send, X } from 'lucide-react'
import { Sheet, SheetContent, SheetTitle } from '../../lib/shadcn/sheet'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Textarea } from '../../lib/shadcn/textarea'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Spinner } from '../../lib/shadcn/spinner'
import { Chip } from '../interviews/Chip'
import {
  useAddGrievanceNote, useAddGrievanceParty, useAnswerGrievance, useConfirmGrievanceIdentity, useEscalateGrievance, useGetGrievance, useJoinGrievances,
  useOpenGrievanceFile, useReclassifyGrievance, useRecordGrievanceCcma, useRecordGrievanceLate, useRecordGrievanceMeeting,
  useSetGrievanceHarassmentRoute, useWithdrawGrievance,
} from '../../hooks/backend/grievances'
import { useGrievanceUpload } from '../../hooks/useGrievanceUpload'
import {
  CHANNEL_LABEL, STAGE_HELP, STATUS_LABEL, answerMissing, formatDay, formatWhen, todaySast,
  type Attachment, type Detail, type Options, type Row,
} from '../../utils/grievances'

function Section({ title, children, tone }: { title: string; children: ReactNode; tone?: 'brand' | 'amber' }) {
  return (
    <section className={`space-y-2 rounded-lg border p-3 ${tone === 'brand' ? 'border-[hsl(var(--brand))]/40 bg-[hsl(var(--brand-soft))]/30' : tone === 'amber' ? 'border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/40' : 'border-border'}`}>
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</h3>
      {children}
    </section>
  )
}

function L({ label, htmlFor, children }: { label: string; htmlFor: string; children: ReactNode }) {
  return (
    <div className="space-y-1">
      <label htmlFor={htmlFor} className="text-xs font-medium text-foreground">{label}</label>
      {children}
    </div>
  )
}

export function GrievanceDetail({ id, onClose, onChanged, options, openRows }: {
  id: string | null; onClose: () => void; onChanged: () => void; options: Options | null; openRows: Row[]
}) {
  const get = useGetGrievance<Detail>()
  const load = get.trigger
  const refresh = useCallback(() => { if (id) void load({ id }, { skipCache: true }).catch(() => undefined) }, [id, load])
  useEffect(() => { refresh() }, [refresh])
  const d = id ? get.data : null
  const changed = () => { refresh(); onChanged() }

  return (
    <Sheet open={!!id} onOpenChange={(o) => { if (!o) onClose() }}>
      <SheetContent side="right" className="w-full overflow-y-auto p-0 sm:max-w-2xl">
        <SheetTitle className="sr-only">Grievance</SheetTitle>
        {!d || d.row.id !== id ? (
          <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
            {get.error ? <span className="text-rose-700">{get.error}</span> : <><Spinner className="h-4 w-4" /> Loading…</>}
          </div>
        ) : d.restricted ? (
          <Restricted d={d} />
        ) : (
          <Full d={d} onChanged={changed} options={options} openRows={openRows} />
        )}
      </SheetContent>
    </Sheet>
  )
}

function Head({ d }: { d: Detail }) {
  const r = d.row
  return (
    <div className="space-y-2 border-b border-border px-5 pb-4 pt-5">
      <div className="flex flex-wrap items-center gap-2 pr-8">
        <span className="font-mono text-sm font-semibold text-foreground">{r.ref}</span>
        <Chip tone={r.status === 'open' ? (r.overdue ? 'red' : 'amber') : r.status === 'accepted' ? 'green' : 'neutral'}>
          {r.status === 'open' && r.overdue ? 'Overdue' : STATUS_LABEL[r.status]}
        </Chip>
        <Chip tone="brand">{r.stage_label}</Chip>
        {r.confidential && <Chip tone="neutral" icon={<Lock className="h-3 w-3" />}>Confidential</Chip>}
        {r.collective && <Chip tone="neutral">Collective</Chip>}
        {r.is_test && <Chip tone="neutral">Test</Chip>}
      </div>
      <p className="text-xs text-muted-foreground">
        {r.holder ? <>Held by <b className="text-foreground">{r.holder}</b>.</> : null}
        {r.meet_by && !r.met_on ? ` Meet by ${formatDay(r.meet_by)}.` : r.met_on ? ` Met ${formatDay(r.met_on)}.` : ''}
        {r.answer_by && r.status === 'open' ? ` Answer by ${formatDay(r.answer_by)}.` : ''}
        {' '}{STAGE_HELP[r.stage] ?? ''}
      </p>
    </div>
  )
}

function Restricted({ d }: { d: Detail }) {
  return (
    <div>
      <Head d={d} />
      <div className="flex items-start gap-3 p-5 text-sm text-muted-foreground">
        <Lock className="mt-0.5 h-5 w-5 shrink-0" />
        <p>{d.message}</p>
      </div>
    </div>
  )
}

function Full({ d, onChanged, options, openRows }: { d: Detail; onChanged: () => void; options: Options | null; openRows: Row[] }) {
  const r = d.row
  const can = d.can!
  const openFile = useOpenGrievanceFile<{ url: string }>()
  const [err, setErr] = useState<string | null>(null)

  async function open(a: Attachment) {
    if (!a.path) return
    setErr(null)
    const w = window.open('', '_blank')
    try {
      const x = await openFile.trigger({ path: a.path }, { skipCache: true })
      if (x?.url && w) w.location.href = x.url
      else w?.close()
    } catch (e) { w?.close(); setErr(e instanceof Error ? e.message : String(e)) }
  }

  return (
    <div>
      <Head d={d} />
      <div className="space-y-4 p-5">
        {err && <p className="rounded-md bg-rose-50 p-2 text-sm text-rose-800 dark:bg-rose-950 dark:text-rose-200">{err}</p>}

        <Section title="The grievance">
          <p className="text-sm font-medium text-foreground">{r.employee ?? 'Name not given'}{r.category_label ? `, ${r.category_label.toLowerCase()}` : ''}</p>
          <p className="whitespace-pre-wrap text-sm text-foreground">{d.description}</p>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground">
            <dt>Raised</dt><dd>{formatWhen(r.raised_at)}, {CHANNEL_LABEL[r.channel ?? ''] ?? r.channel}{d.language ? `, in ${d.language}` : ''}</dd>
            <dt>It happened</dt><dd>{formatDay(d.event_date)}{r.late ? ', more than 30 days before' : ''}</dd>
            <dt>About</dt><dd>{d.subject ?? 'Nobody in particular'}</dd>
            <dt>Recorded by</dt><dd>{d.recorded_by ?? 'Himself'}{d.read_back_confirmed ? ', read back and confirmed' : ''}</dd>
            <dt>Written to</dt><dd>{d.employee_email ?? 'No email address: letters are owed to him in writing'}</dd>
            <dt>Why this holder</dt><dd>Clause {d.route_rule}. {d.route_reason}</dd>
          </dl>
          {r.ccma_referred_on && <p className="text-xs text-foreground"><Scale className="mr-1 inline h-3.5 w-3.5" />He referred a dispute to the CCMA on {formatDay(r.ccma_referred_on)}. It is on the CCMA and Disputes page.</p>}
          {(d.joined ?? []).length > 0 && <p className="text-xs text-foreground">Joined to this one (31.11): {(d.joined ?? []).map((j) => `${j.ref} ${j.employee ?? ''}`).join(', ')}.</p>}
          {(d.parties ?? []).length > 0 && <p className="text-xs text-muted-foreground">With him: {(d.parties ?? []).map((p) => `${p.name} (${p.role.replace(/_/g, ' ')})`).join(', ')}.</p>}
        </Section>

        {can.late && <LateStep d={d} onChanged={onChanged} />}
        {can.harassment_route && <HarassmentStep d={d} onChanged={onChanged} />}
        {can.meeting && <MeetingStep d={d} onChanged={onChanged} />}
        {can.answer && <AnswerStep d={d} onChanged={onChanged} />}

        {(d.answers ?? []).map((a) => (
          <Section key={a.id} title={`Written answer, ${a.stage.replace('_', ' ')}, ${formatWhen(a.sent_at)}`}>
            <p className="text-xs text-muted-foreground">By {a.decider}{a.meeting_on ? `, after meeting him on ${formatDay(a.meeting_on)}` : ''}{a.companion ? `. With him: ${a.companion}` : ''}{a.interpreter ? `. Interpreter: ${a.interpreter}` : ''}.</p>
            <p className="text-sm"><b>Found.</b> {a.found}</p>
            <p className="text-sm"><b>Fleetly will.</b> {a.will_do} By {formatDay(a.will_do_by)}.</p>
            <p className="text-sm"><b>Next step.</b> {a.next_step}</p>
            <p className="text-xs text-muted-foreground">{a.response === 'accepted' ? `He accepted it, ${formatWhen(a.responded_at)}.` : a.response === 'escalated' ? `He escalated it, ${formatWhen(a.responded_at)}.` : 'He has not answered yet.'}</p>
          </Section>
        ))}

        {(d.actions ?? []).length > 0 && (
          <Section title="What Fleetly promised">
            <ul className="space-y-1 text-sm">
              {(d.actions ?? []).map((a, i) => (
                <li key={i} className="flex flex-wrap items-center gap-2">
                  <span className="flex-1">{a.action}</span>
                  <span className="text-xs text-muted-foreground">by {formatDay(a.due_on)}</span>
                  {a.task_status && <Chip tone={a.task_status === 'Done' ? 'green' : 'neutral'}>{a.task_status}</Chip>}
                </li>
              ))}
            </ul>
          </Section>
        )}

        <Timeline d={d} onOpen={open} />

        <MoreSteps d={d} onChanged={onChanged} options={options} openRows={openRows} />
      </div>
    </div>
  )
}

function useRun(onChanged: () => void) {
  const [error, setError] = useState<string | null>(null)
  const run = async (fn: () => Promise<unknown>) => {
    setError(null)
    try { await fn(); onChanged(); return true } catch (e) { setError(e instanceof Error ? e.message : String(e)); return false }
  }
  return { error, run, setError }
}

function Err({ error }: { error: string | null }) {
  return error ? <p className="rounded-md bg-rose-50 p-2 text-xs text-rose-800 dark:bg-rose-950 dark:text-rose-200">{error}</p> : null
}

function LateStep({ d, onChanged }: { d: Detail; onChanged: () => void }) {
  const late = useRecordGrievanceLate()
  const { error, run } = useRun(onChanged)
  const [why, setWhy] = useState('')
  return (
    <Section title="First: why was it raised late? (31.3)" tone="amber">
      <p className="text-xs">It was raised more than 30 days after the event. Ask him why, write down what he says, and decide whether it is a reasonable explanation. Either way he is answered in writing.</p>
      <Textarea rows={3} value={why} onChange={(e) => setWhy(e.target.value)} aria-label="His explanation" placeholder="His explanation, in his words" />
      <Err error={error} />
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="brand" disabled={!why.trim() || late.loading} onClick={() => void run(() => late.trigger({ id: d.row.id, explanation: why, accepted: true }))}>Accept the explanation</Button>
        <Button size="sm" variant="outline" disabled={!why.trim() || late.loading} onClick={() => void run(() => late.trigger({ id: d.row.id, explanation: why, accepted: false }))}>Not reasonable</Button>
      </div>
    </Section>
  )
}

function HarassmentStep({ d, onChanged }: { d: Detail; onChanged: () => void }) {
  const set = useSetGrievanceHarassmentRoute()
  const { error, run } = useRun(onChanged)
  return (
    <Section title="First: the route he chooses (31.10)" tone="amber">
      <p className="text-xs">A harassment complaint is investigated confidentially under the Code of Good Practice on the Prevention and Elimination of Harassment in the Workplace. He chooses the route, not Fleetly.</p>
      <Err error={error} />
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" disabled={set.loading} onClick={() => void run(() => set.trigger({ id: d.row.id, route: 'informal' }))}>He chose informal</Button>
        <Button size="sm" variant="outline" disabled={set.loading} onClick={() => void run(() => set.trigger({ id: d.row.id, route: 'formal' }))}>He chose formal</Button>
      </div>
    </Section>
  )
}

function MeetingStep({ d, onChanged }: { d: Detail; onChanged: () => void }) {
  const meet = useRecordGrievanceMeeting()
  const { error, run } = useRun(onChanged)
  const [m, setM] = useState({ met_on: todaySast(), companion: '', union_representative: '', interpreter: '', note: '' })
  return (
    <Section title="Record the meeting">
      <div className="grid gap-2 sm:grid-cols-2">
        <L label="The day you met him" htmlFor="m-on"><Input id="m-on" type="date" max={todaySast()} value={m.met_on} onChange={(e) => setM({ ...m, met_on: e.target.value })} /></L>
        <L label="Fellow employee with him" htmlFor="m-comp"><Input id="m-comp" value={m.companion} onChange={(e) => setM({ ...m, companion: e.target.value })} /></L>
        <L label="Trade union representative" htmlFor="m-union"><Input id="m-union" value={m.union_representative} onChange={(e) => setM({ ...m, union_representative: e.target.value })} /></L>
        <L label="Interpreter" htmlFor="m-int"><Input id="m-int" value={m.interpreter} onChange={(e) => setM({ ...m, interpreter: e.target.value })} /></L>
      </div>
      <Textarea rows={2} value={m.note} onChange={(e) => setM({ ...m, note: e.target.value })} aria-label="Note" placeholder="What was said, briefly (optional)" />
      <Err error={error} />
      <Button size="sm" variant="outline" disabled={!m.met_on || meet.loading} onClick={() => void run(() => meet.trigger({ id: d.row.id, ...m }))}>Record the meeting</Button>
    </Section>
  )
}

function AnswerStep({ d, onChanged }: { d: Detail; onChanged: () => void }) {
  const answer = useAnswerGrievance()
  const { error, run } = useRun(onChanged)
  const [a, setA] = useState({ meeting_on: d.row.met_on ?? '', found: '', will_do: '', will_do_by: '', next_step: '', companion: '', interpreter: '' })
  const [actions, setActions] = useState<{ action: string; due_on: string }[]>([])
  const missing = answerMissing(a, d.row.stage, d.row.met_on)
  const final = d.row.stage !== 'step_one'
  return (
    <Section title="The written answer (31.12)" tone="brand">
      <p className="text-xs text-muted-foreground">
        He receives this by email with "I accept"{final ? '' : ' and "I want to escalate"'}, and with his right to go to the CCMA at any time. Every promise becomes a task with its date.
      </p>
      {!d.row.met_on && (
        <L label="The day you met him" htmlFor="a-met"><Input id="a-met" type="date" max={todaySast()} value={a.meeting_on} onChange={(e) => setA({ ...a, meeting_on: e.target.value })} /></L>
      )}
      <L label="What was found" htmlFor="a-found"><Textarea id="a-found" rows={3} value={a.found} onChange={(e) => setA({ ...a, found: e.target.value })} /></L>
      <div className="grid gap-2 sm:grid-cols-[1fr_11rem]">
        <L label="What Fleetly will do" htmlFor="a-will"><Textarea id="a-will" rows={2} value={a.will_do} onChange={(e) => setA({ ...a, will_do: e.target.value })} /></L>
        <L label="By when" htmlFor="a-by"><Input id="a-by" type="date" min={todaySast()} value={a.will_do_by} onChange={(e) => setA({ ...a, will_do_by: e.target.value })} /></L>
      </div>
      {actions.map((x, i) => (
        <div key={i} className="grid gap-2 sm:grid-cols-[1fr_11rem_2rem]">
          <Input aria-label={`Promise ${i + 2}`} value={x.action} placeholder="Another thing Fleetly will do" onChange={(e) => setActions(actions.map((y, j) => j === i ? { ...y, action: e.target.value } : y))} />
          <Input aria-label={`Promise ${i + 2} date`} type="date" min={todaySast()} value={x.due_on} onChange={(e) => setActions(actions.map((y, j) => j === i ? { ...y, due_on: e.target.value } : y))} />
          <button type="button" aria-label="Remove" onClick={() => setActions(actions.filter((_, j) => j !== i))}><X className="h-4 w-4" /></button>
        </div>
      ))}
      <button type="button" className="text-xs text-[hsl(var(--brand))]" onClick={() => setActions([...actions, { action: '', due_on: '' }])}><Plus className="mr-1 inline h-3 w-3" />Another promise</button>
      <L label="The next step" htmlFor="a-next">
        <Textarea id="a-next" rows={2} value={a.next_step} onChange={(e) => setA({ ...a, next_step: e.target.value })}
          placeholder={final ? 'This answer is final within Fleetly. He may refer a dispute to the CCMA at any time.' : 'If he is not satisfied, he may escalate to the Operational Servant.'} />
      </L>
      <div className="grid gap-2 sm:grid-cols-2">
        <L label="Who attended with him" htmlFor="a-comp"><Input id="a-comp" value={a.companion} onChange={(e) => setA({ ...a, companion: e.target.value })} /></L>
        <L label="Interpreter" htmlFor="a-int"><Input id="a-int" value={a.interpreter} onChange={(e) => setA({ ...a, interpreter: e.target.value })} /></L>
      </div>
      <Err error={error} />
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="brand" disabled={missing.length > 0 || answer.loading}
          onClick={() => void run(() => answer.trigger({ id: d.row.id, ...a, meeting_on: a.meeting_on || null, actions: actions.filter((x) => x.action.trim() && x.due_on) }))}>
          {answer.loading ? <Spinner className="mr-1 h-4 w-4" /> : <Send className="mr-1 h-4 w-4" />} Send the answer
        </Button>
        {missing.length > 0 && <span className="text-xs text-muted-foreground">Cannot be sent until you fill {missing.join(', ')}.</span>}
      </div>
    </Section>
  )
}

const EVENT_LABEL: Record<string, string> = {
  raised: 'Raised', routed: 'Routed', acknowledged: 'Acknowledged', task_created: 'Task', meeting: 'Meeting', answered: 'Answered',
  answer_sent: 'Letter', escalated: 'Escalated', escalation_confirmed: 'Letter', accepted: 'Accepted', late: 'Late',
  late_decided: 'Late explanation', harassment_route: 'Route chosen', deadline_missed: 'Overdue', meeting_missed: 'Meeting overdue',
  may_escalate_sent: 'Letter', overdue_flagged: 'Flagged', written_copy_owed: 'Written copy owed', written_copy_given: 'Given in writing',
  note: 'Note', ccma_referral: 'CCMA', withdrawn: 'Withdrawn', joined: 'Joined', collective: 'Collective', party_added: 'Joined him',
  reclassified: 'Corrected', rerouted: 'Letter', decision_checked: 'Decision checked', hearing_chair_replaced: 'Hearing chair',
  employee_wrote: 'He wrote', to_classify: 'To classify', identity_to_confirm: 'Identity', identity_confirmed: 'Identity confirmed', notice_sent: 'Notice', recorded_after_the_day: 'Recorded late',
}

function Timeline({ d, onOpen }: { d: Detail; onOpen: (a: Attachment) => void }) {
  return (
    <Section title="Every step">
      <ol className="space-y-2">
        {(d.events ?? []).filter((e) => e.type !== 'task_created').map((e) => (
          <li key={e.id} className="grid grid-cols-[6.5rem_1fr] gap-2 text-xs">
            <span className="text-muted-foreground">{formatWhen(e.at)}</span>
            <div className="space-y-1">
              <p>
                <span className={`mr-1 font-semibold ${['deadline_missed', 'meeting_missed', 'written_copy_owed', 'overdue_flagged'].includes(e.type) ? 'text-rose-700 dark:text-rose-300' : 'text-foreground'}`}>{EVENT_LABEL[e.type] ?? e.type}</span>
                <span className="text-muted-foreground">{e.who ? `${e.who}. ` : ''}</span>
                <span className="whitespace-pre-wrap text-foreground">{e.what}</span>
                {e.message_status && <span className="ml-1"><Mail className="inline h-3 w-3" /> {e.message_status}</span>}
              </p>
              {(e.attachments ?? []).map((a, i) => a.path ? (
                <button key={i} type="button" className="mr-2 inline-flex items-center gap-1 text-[hsl(var(--brand))] hover:underline" onClick={() => onOpen(a)}>
                  <FileText className="h-3 w-3" /> {a.filename ?? 'File'} <ExternalLink className="h-3 w-3" />
                </button>
              ) : a.kind === 'whatsapp' ? (
                <p key={i} className="rounded border border-border bg-muted/40 p-2">WhatsApp {a.type} from {a.from}: {a.body || '(open it in the WhatsApp inbox to play it)'}</p>
              ) : null)}
            </div>
          </li>
        ))}
      </ol>
    </Section>
  )
}

function MoreSteps({ d, onChanged, options, openRows }: { d: Detail; onChanged: () => void; options: Options | null; openRows: Row[] }) {
  const can = d.can!
  const [which, setWhich] = useState<string>('')
  // Offered while the form's "identity not confirmed" step is open. Postgres
  // decides who may confirm and refuses a grievance that is already linked.
  const events = d.events ?? []
  const identityOpen = events.some((e) => e.type === 'identity_to_confirm') && !events.some((e) => e.type === 'identity_confirmed')
  const steps: { key: string; label: string; show: boolean }[] = [
    { key: 'identity', label: 'Confirm who raised it', show: identityOpen },
    { key: 'note', label: 'Add a note, a document, or a letter given to him', show: true },
    { key: 'party', label: 'Someone joins him (companion, union, interpreter)', show: d.row.status === 'open' },
    { key: 'escalate', label: 'He asked to escalate', show: can.escalate_on_request },
    { key: 'reclassify', label: 'Correct what it is about', show: can.reclassify },
    { key: 'collective', label: 'Join other grievances to this one (31.11)', show: can.collective },
    { key: 'ccma', label: 'He referred a dispute to the CCMA', show: can.ccma },
    { key: 'withdraw', label: 'He withdrew it in writing', show: can.withdraw },
  ].filter((s) => s.show)
  return (
    <Section title="Other steps">
      <NativeSelect value={which} onChange={(e) => setWhich(e.target.value)} aria-label="Other steps">
        <option value="">Choose a step to record</option>
        {steps.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
      </NativeSelect>
      {which === 'identity' && <IdentityStep d={d} options={options} onChanged={() => { setWhich(''); onChanged() }} />}
      {which === 'note' && <NoteStep d={d} onChanged={() => { setWhich(''); onChanged() }} />}
      {which === 'party' && <PartyStep d={d} onChanged={() => { setWhich(''); onChanged() }} />}
      {which === 'escalate' && <EscalateStep d={d} onChanged={() => { setWhich(''); onChanged() }} />}
      {which === 'reclassify' && <ReclassifyStep d={d} options={options} onChanged={() => { setWhich(''); onChanged() }} />}
      {which === 'collective' && <CollectiveStep d={d} openRows={openRows} onChanged={() => { setWhich(''); onChanged() }} />}
      {which === 'ccma' && <CcmaStep d={d} onChanged={() => { setWhich(''); onChanged() }} />}
      {which === 'withdraw' && <WithdrawStep d={d} onChanged={() => { setWhich(''); onChanged() }} />}
    </Section>
  )
}

function Files({ files, setFiles, id }: { files: File[]; setFiles: (f: File[]) => void; id: string }) {
  return (
    <>
      <label htmlFor={id} className="flex cursor-pointer items-center gap-2 rounded-md border border-dashed border-border px-3 py-2 text-xs text-muted-foreground hover:bg-accent/40">
        <Paperclip className="h-4 w-4" /> {files.length ? files.map((x) => x.name).join(', ') : 'Attach files'}
      </label>
      <input id={id} type="file" multiple className="sr-only" onChange={(e) => setFiles(Array.from(e.target.files ?? []))} />
    </>
  )
}

function IdentityStep({ d, options, onChanged }: { d: Detail; options: Options | null; onChanged: () => void }) {
  const confirm = useConfirmGrievanceIdentity<{ employee: string }>()
  const { error, run } = useRun(onChanged)
  const [personId, setPersonId] = useState('')
  const [how, setHow] = useState('')
  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        It came in on the form without his own link, so it is not linked to anyone yet. A phone number is not proof:
        ask him directly, on the number on his record, whether he raised it. Confirming links it to him and sends nothing.
      </p>
      <NativeSelect value={personId} onChange={(e) => setPersonId(e.target.value)} aria-label="Who raised it">
        <option value="">Choose who raised it</option>
        {(options?.employees ?? []).map((p) => <option key={p.person_id} value={p.person_id}>{p.name}{p.kind === 'staff' ? ' (staff)' : ''}</option>)}
      </NativeSelect>
      <Input value={how} onChange={(e) => setHow(e.target.value)} aria-label="How you confirmed it"
        placeholder='How you confirmed it, for example "Called him at 14:10, he confirmed he raised it"' />
      <Err error={error} />
      <Button size="sm" variant="outline" disabled={!personId || how.trim().length < 10 || confirm.loading}
        onClick={() => void run(() => confirm.trigger({ id: d.row.id, person_id: personId, how }))}>Confirm</Button>
    </div>
  )
}

function NoteStep({ d, onChanged }: { d: Detail; onChanged: () => void }) {
  const note = useAddGrievanceNote()
  const { upload, uploading } = useGrievanceUpload()
  const { error, run } = useRun(onChanged)
  const [kind, setKind] = useState('note')
  const [text, setText] = useState('')
  const [files, setFiles] = useState<File[]>([])
  return (
    <div className="space-y-2">
      <NativeSelect value={kind} onChange={(e) => setKind(e.target.value)} aria-label="Kind">
        <option value="note">A note or a document</option>
        <option value="written_copy_given">I gave him a letter in writing (he has no email)</option>
      </NativeSelect>
      <Textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} aria-label="Note"
        placeholder={kind === 'written_copy_given' ? 'Which letter, how and when, for example "Answer sent on WhatsApp at 14:10"' : 'The note'} />
      <Files files={files} setFiles={setFiles} id="n-files" />
      <Err error={error} />
      <Button size="sm" variant="outline" disabled={(!text.trim() && files.length === 0) || note.loading || uploading}
        onClick={() => void run(async () => {
          const up = await upload(files, d.row.id)
          if (up.failed.length) throw new Error(up.failed.join(' '))
          await note.trigger({ id: d.row.id, kind, note: text, attachments: up.done })
        })}>Save</Button>
    </div>
  )
}

function PartyStep({ d, onChanged }: { d: Detail; onChanged: () => void }) {
  const add = useAddGrievanceParty()
  const { error, run } = useRun(onChanged)
  const [role, setRole] = useState('companion')
  const [name, setName] = useState('')
  return (
    <div className="space-y-2">
      <div className="grid gap-2 sm:grid-cols-2">
        <NativeSelect value={role} onChange={(e) => setRole(e.target.value)} aria-label="Role">
          <option value="companion">Fellow employee</option>
          <option value="union_representative">Trade union representative</option>
          <option value="interpreter">Interpreter</option>
          <option value="representative">Representative of a collective grievance</option>
        </NativeSelect>
        <Input value={name} onChange={(e) => setName(e.target.value)} aria-label="Name" placeholder="Their name" />
      </div>
      <Err error={error} />
      <Button size="sm" variant="outline" disabled={!name.trim() || add.loading} onClick={() => void run(() => add.trigger({ id: d.row.id, role, name }))}>Add</Button>
    </div>
  )
}

function EscalateStep({ d, onChanged }: { d: Detail; onChanged: () => void }) {
  const esc = useEscalateGrievance()
  const { error, run } = useRun(onChanged)
  const [how, setHow] = useState('')
  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">It goes to the Operational Servant with its full history, and he is told in writing (31.5).</p>
      <Input value={how} onChange={(e) => setHow(e.target.value)} aria-label="How he asked" placeholder='How and when he asked, for example "WhatsApp to Kabelo, 14:10 today"' />
      <Err error={error} />
      <Button size="sm" variant="brand" disabled={!how.trim() || esc.loading} onClick={() => void run(() => esc.trigger({ id: d.row.id, how }))}>Escalate it</Button>
    </div>
  )
}

function ReclassifyStep({ d, options, onChanged }: { d: Detail; options: Options | null; onChanged: () => void }) {
  const re = useReclassifyGrievance<{ moved: boolean; owner_name: string }>()
  const { error, run } = useRun(onChanged)
  const [c, setC] = useState({ category: d.row.category ?? 'other', urgent: d.row.urgent === true, harassment: d.row.harassment === true, subject_person_id: '', reason: '' })
  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">If this changes who should answer it under clause 31, it moves to them and he is told.</p>
      <div className="grid gap-2 sm:grid-cols-2">
        <NativeSelect value={c.category} onChange={(e) => setC({ ...c, category: e.target.value })} aria-label="Category">
          {(options?.categories ?? []).map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
        </NativeSelect>
        <NativeSelect value={c.subject_person_id} onChange={(e) => setC({ ...c, subject_person_id: e.target.value })} aria-label="About">
          <option value="">About: leave as it is</option>
          {(options?.staff ?? []).map((s) => <option key={s.person_id} value={s.person_id}>About {s.name}</option>)}
        </NativeSelect>
      </div>
      <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={c.urgent} onChange={(e) => setC({ ...c, urgent: e.target.checked })} /> Urgent (31.9)</label>
      <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={c.harassment} onChange={(e) => setC({ ...c, harassment: e.target.checked })} /> Harassment (31.10)</label>
      <Input value={c.reason} onChange={(e) => setC({ ...c, reason: e.target.value })} aria-label="Why" placeholder="Why it is being corrected" />
      <Err error={error} />
      <Button size="sm" variant="outline" disabled={!c.reason.trim() || re.loading}
        onClick={() => void run(() => re.trigger({ id: d.row.id, ...c, subject_person_id: c.subject_person_id || undefined }))}>Correct it</Button>
    </div>
  )
}

function CollectiveStep({ d, openRows, onChanged }: { d: Detail; openRows: Row[]; onChanged: () => void }) {
  const join = useJoinGrievances()
  const { error, run } = useRun(onChanged)
  const [picked, setPicked] = useState<string[]>([])
  const [reps, setReps] = useState(['', '', ''])
  const candidates = openRows.filter((r) => r.id !== d.row.id && r.full && !r.confidential && !r.collective_parent_id)
  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">The same concern from several employees: meet them together with up to 3 representatives they choose, and send one written answer to all (31.11).</p>
      {candidates.length === 0 ? <p className="text-xs">No other open grievance can be joined.</p> : (
        <ul className="max-h-40 space-y-1 overflow-y-auto text-xs">
          {candidates.map((r) => (
            <li key={r.id}><label className="flex items-center gap-2">
              <input type="checkbox" checked={picked.includes(r.id)} onChange={(e) => setPicked(e.target.checked ? [...picked, r.id] : picked.filter((x) => x !== r.id))} />
              {r.ref}, {r.employee}, {r.category_label}
            </label></li>
          ))}
        </ul>
      )}
      <div className="grid gap-2 sm:grid-cols-3">
        {reps.map((x, i) => <Input key={i} value={x} aria-label={`Representative ${i + 1}`} placeholder={`Representative ${i + 1}`} onChange={(e) => setReps(reps.map((y, j) => j === i ? e.target.value : y))} />)}
      </div>
      <Err error={error} />
      <Button size="sm" variant="outline" disabled={picked.length === 0 || join.loading}
        onClick={() => void run(() => join.trigger({ id: d.row.id, children: picked, representatives: reps.filter((x) => x.trim()) }))}>Join them</Button>
    </div>
  )
}

function CcmaStep({ d, onChanged }: { d: Detail; onChanged: () => void }) {
  const ccma = useRecordGrievanceCcma()
  const { error, run } = useRun(onChanged)
  const [c, setC] = useState({ referred_on: todaySast(), case_number: '', note: '' })
  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">That is his right at any time (31.16). It is recorded on the CCMA and Disputes page, and the grievance carries on unless he withdraws it in writing. Nobody asks him to withdraw a referral.</p>
      <div className="grid gap-2 sm:grid-cols-2">
        <Input type="date" max={todaySast()} value={c.referred_on} onChange={(e) => setC({ ...c, referred_on: e.target.value })} aria-label="Referred on" />
        <Input value={c.case_number} onChange={(e) => setC({ ...c, case_number: e.target.value })} aria-label="Case number" placeholder="CCMA case number, if known" />
      </div>
      <Input value={c.note} onChange={(e) => setC({ ...c, note: e.target.value })} aria-label="Note" placeholder="How we heard" />
      <Err error={error} />
      <Button size="sm" variant="outline" disabled={!c.referred_on || ccma.loading} onClick={() => void run(() => ccma.trigger({ id: d.row.id, ...c }))}>Record the referral</Button>
    </div>
  )
}

function WithdrawStep({ d, onChanged }: { d: Detail; onChanged: () => void }) {
  const wd = useWithdrawGrievance()
  const { upload, uploading } = useGrievanceUpload()
  const { error, run } = useRun(onChanged)
  const [how, setHow] = useState('')
  const [files, setFiles] = useState<File[]>([])
  return (
    <div className="space-y-2">
      <p className="flex items-start gap-1 text-xs text-amber-800 dark:text-amber-200"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />Only he can withdraw it, and only in writing. Attach his message or signed note.</p>
      <Input value={how} onChange={(e) => setHow(e.target.value)} aria-label="How" placeholder="How it reached you, for example his WhatsApp of 2 October" />
      <Files files={files} setFiles={setFiles} id="w-files" />
      <Err error={error} />
      <Button size="sm" variant="outline" disabled={!how.trim() || files.length === 0 || wd.loading || uploading}
        onClick={() => void run(async () => {
          const up = await upload(files, d.row.id)
          if (up.failed.length) throw new Error(up.failed.join(' '))
          await wd.trigger({ id: d.row.id, how, attachments: up.done })
        })}>Record the withdrawal</Button>
    </div>
  )
}
